import {
  MessagesAndDate,
  ServiceStateStrictWithEffects,
  SourcedMessage,
  TMCoreAction,
  TopicChangeRequest,
} from './types';
import { ServiceStateStrict } from 'state/service/app';
import {
  buildCommMessageForActions,
  communicatorSend,
  markAllAsSeen,
  modelWithEffects,
  modelWithNoEffects,
  sameEventStream,
  updateHistory,
  updateModelWithLocalActions,
  updateModelWithSentChat,
} from './helpers';
import { update } from 'state/service/appTs/tmCore';
import ServerConnectionState from 'constants/service/serverConnectionState';
import defaultAppState from 'defaultAppState';

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L159-L160
 */
export function clearError(model: ServiceStateStrict): ServiceStateStrict {
  return { ...model, error: null, decodeError: null };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L162-L163
 */
export function displayDecodeError(model: ServiceStateStrict, message: string): ServiceStateStrict {
  return { ...model, decodeError: message };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L165-L166
 */
export function displayError(model: ServiceStateStrict, message: string): ServiceStateStrict {
  return { ...model, error: message };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L168-L169
 */
export function markHistoryItemsAsRead(model: ServiceStateStrict): ServiceStateStrict {
  return { ...model, history: markAllAsSeen(model.history) };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L171-L172
 */
export function initAppState(
  _model: ServiceStateStrict,
  initialModel: ServiceStateStrict
): ServiceStateStrict {
  return initialModel;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L174-L175
 */
export function sendModel(model: ServiceStateStrict): ServiceStateStrict {
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L177-L178
 */
export function setIsReadOnly(model: ServiceStateStrict, isReadOnly: boolean): ServiceStateStrict {
  return { ...model, isReadOnly };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L180-L181
 */
export function setHistoryToggledOn(
  model: ServiceStateStrict,
  toggledOn: boolean
): ServiceStateStrict {
  return updateHistory({ ...model, historyToggledOn: toggledOn });
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L183-L184
 */
export function setTopic(
  oldModel: ServiceStateStrict,
  topicChangeRequest: TopicChangeRequest
): ServiceStateStrict {
  const { newTopic, existingCommunicator, model } = topicChangeRequest;
  const newCommunicator = existingCommunicator || defaultAppState.communicator;
  const newModel = model || {
    ...oldModel,
    app: defaultAppState.app,
    currentTopic: newTopic,
    communicator: newCommunicator,
    history: { ...defaultAppState.history, isVisible: oldModel.historyToggledOn },
  };
  if (!oldModel.currentTopic) {
    return newModel;
  }
  if (sameEventStream(oldModel.currentTopic, newTopic)) {
    return { ...oldModel, currentTopic: newTopic };
  }
  return newModel;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L186-L210
 */
export function localAction(
  model: ServiceStateStrict,
  timestampSecs: number,
  actions: TMCoreAction[]
): ServiceStateStrictWithEffects {
  if (model.isReadOnly) {
    return modelWithNoEffects(model);
  }

  try {
    const updatedModel = updateModelWithLocalActions(model, timestampSecs, actions);
    const commMessage = buildCommMessageForActions(actions);
    const [effects, communicator] = communicatorSend(commMessage, updatedModel.communicator);
    return modelWithEffects({ ...updatedModel, communicator }, effects);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    // TODO check type of error
    return modelWithNoEffects({ ...model, error: e.displayMessage });
  }
}

/**
 * Don't send the message as it caused an error to model update
 *
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L212-L224
 */
export function sendChat(
  model: ServiceStateStrict,
  timestampSecs: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  chatMessage: any // TODO
): ServiceStateStrict {
  const communicator = communicatorSend(chatMessage, model.communicator);
  const updatedModel = { ...model, communicator };
  return updateModelWithSentChat(timestampSecs, chatMessage, updatedModel);
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L226-L280
 */
export function applyMessages(
  model: ServiceStateStrict,
  messages: SourcedMessage[]
): ServiceStateStrict {
  // TODO: actual implementation. This is just a stub to be able to test restaurant model logic.
  let updatedApp = model.app;
  for (const message of messages) {
    if (message.type === 'SourcedMessage') {
      const commMessage = message.commMessage;
      if (commMessage.type === 'Batch') {
        for (const action of commMessage.actions) {
          if (action.type === 'Core') {
            updatedApp = update(updatedApp, action.action);
          }
        }
      }
    }
  }
  return { ...model, app: updatedApp };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L282-L283
 */
export function fetchMoreHistory(model: ServiceStateStrict): ServiceStateStrict {
  // TODO
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L285-L286
 */
export function fetchHistoryFailed(model: ServiceStateStrict): ServiceStateStrict {
  return { ...model, history: { ...model.history, isLoading: false } };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L288-L322
 */
export function loadHistory(
  model: ServiceStateStrict,
  messagesAndDate: MessagesAndDate
): ServiceStateStrict {
  // TODO
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L324-L334
 */
export function setServerConnectionState(
  model: ServiceStateStrict,
  serverConnectionState: keyof typeof ServerConnectionState
): ServiceStateStrict {
  return {
    ...model,
    currentTopic: model.currentTopic && { ...model.currentTopic, everConnected: true },
    serverConnectionState,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L336-L341
 */
export function acceptWebsocketMessage(model: ServiceStateStrict): ServiceStateStrict {
  // TODO
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L343-L357
 */
export function rejectWebsocketMessage(model: ServiceStateStrict): ServiceStateStrict {
  // TODO
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L359-L360
 */
export function abortWebsocketMessage(model: ServiceStateStrict): ServiceStateStrict {
  // TODO
  return model;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMClient.elm#L362-L367
 */
export function websocketReady(model: ServiceStateStrict): ServiceStateStrict {
  // TODO
  return model;
}
